import PersonIcon from '@mui/icons-material/Person';
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { Products } from 'enums';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiSpinningLoader from 'features/ui/base-components/pai-spinning-loader';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiRoundTextField from 'features/ui/base-components/pai-round-text-field';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { User } from '../account-management.types';

interface ItemProps {
  user: User;
  isChecked: boolean;

  handleSelectedLicenseUsersChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

const UserItem: React.FC<ItemProps> = ({ user, isChecked, handleSelectedLicenseUsersChange }) => {
  const { classes } = useStyles();

  const { firstname, lastname, email } = user;

  return (
    <Stack className={classes.userItem} direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={2}>
      <Stack direction={'row'} alignItems={'center'} gap={1}>
        <PersonIcon className={classes.icon} />
        <Typography className={classes.primaryText}>{`${firstname} ${lastname}`}</Typography>
        <Typography className={classes.secondaryText}>{email}</Typography>
      </Stack>
      <PaiCheckbox checked={isChecked} dense={true} value={user.globalUser} onChange={handleSelectedLicenseUsersChange} />
    </Stack>
  );
};

interface Props {
  open: boolean;
  loading?: boolean;
  stripeProduct: Products | null;

  licenseUsers: string[];
  allUsers: User[];
  allUsersLoading: boolean;

  handleSelectedLicenseUsersChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectAllLicenseUsers(): void;

  handleUnselectAllLicenseUsers(): void;

  handleClose(): void;

  handleSaveLicenseUsers(): void;
}

const ManageLicenseUsersDialog: React.FC<Props> = ({
  open,
  loading,
  stripeProduct,
  licenseUsers,
  allUsers,
  allUsersLoading,
  handleSelectedLicenseUsersChange,
  handleSelectAllLicenseUsers,
  handleUnselectAllLicenseUsers,
  handleClose,
  handleSaveLicenseUsers,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [searchParam, setSearchParam] = useState('');

  useEffect(() => {
    if (open) {
      setSearchParam('');
    }
  }, [open]);

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleClearSearchField = () => {
    setSearchParam('');
  };

  const filteredUsers = useMemo(() => {
    if (!searchParam.trim()) {
      return allUsers;
    }

    const searchLower = searchParam.toLowerCase();
    return allUsers.filter(
      (user) =>
        (user.firstname?.toLowerCase() || '').includes(searchLower) ||
        (user.lastname?.toLowerCase() || '').includes(searchLower) ||
        (user.email?.toLowerCase() || '').includes(searchLower)
    );
  }, [allUsers, searchParam]);

  const isAllUsersSelected = useMemo(() => {
    return licenseUsers.length === allUsers.length;
  }, [licenseUsers, allUsers]);

  const getProductTitle = (product: Products | null) => {
    if (!product) {
      return '';
    }

    switch (product) {
      case Products.SAIA_RECRUITING:
        return 'SAIA Recruiting';
      case Products.SAIA_SALES:
        return 'SAIA Sales';
      case Products.MOTIVE:
        return 'MotivE';
      case Products.LEAD:
        return 'LEaD';
      default:
        return product;
    }
  };

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'sm'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{!stripeProduct ? t('manageLicenseUsers') : t('manageLicenseUsersFromStripe')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {allUsersLoading ? (
          <PaiSpinningLoader />
        ) : (
          <Stack direction={'column'} gap={2}>
            <Stack gap={1}>
              <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Typography className={classes.subtitleText}>{t('manageLicenseUsersSubtitle')}</Typography>
                <PaiHelpIcon tooltipText={t('manageLicenseUsersTooltip')} />
              </Stack>
              {stripeProduct ? (
                <Typography className={classes.descriptionText}>
                  {t('manageLicenseUsersFromStripeDescription', { productName: getProductTitle(stripeProduct) })}
                </Typography>
              ) : null}
            </Stack>
            {allUsers.length >= 5 ? (
              <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                {allUsers.length >= 10 ? (
                  <PaiRoundTextField
                    value={searchParam}
                    variant={'search'}
                    placeholder={t('searchForAUser')}
                    onClear={handleClearSearchField}
                    onChange={handleSearchParamChange}
                    style={{ width: '300px' }}
                  />
                ) : null}
                <PaiCheckbox
                  label={isAllUsersSelected ? t('unselectAll') : t('selectAll')}
                  checked={isAllUsersSelected}
                  indeterminate={!isAllUsersSelected && licenseUsers.length > 0}
                  dense={true}
                  onClick={isAllUsersSelected ? handleUnselectAllLicenseUsers : handleSelectAllLicenseUsers}
                />
              </Stack>
            ) : null}
            <Stack className={classes.userListContainer} gap={1}>
              {filteredUsers.map((user) => (
                <UserItem
                  key={user.id}
                  user={user}
                  isChecked={licenseUsers.includes(user.globalUser)}
                  handleSelectedLicenseUsersChange={handleSelectedLicenseUsersChange}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('save')} border={'rounded'} size={'small'} action={handleSaveLicenseUsers} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
    overflow: 'hidden',
  },
  userItem: {
    backgroundColor: '#FFF',
    borderRadius: '4px',
    padding: '4px 8px',
    border: `1px solid ${theme.palette.divider}`,
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
  },
  primaryText: {
    fontSize: theme.typography.pxToRem(14),
  },
  secondaryText: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  subtitleText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  descriptionText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
  userListContainer: {
    maxHeight: '300px',
    overflowY: 'auto',
  },
}));

export default ManageLicenseUsersDialog;
